import { useEffect, useMemo } from 'react';
import { useForm, type FieldPath, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { Attribute, ProductType } from '@nucleus-commerce/api-client/admin';
import { ApiError, formFieldPath, isApiError } from '@nucleus-commerce/api-client/transport';

import { useAttributes, useProductType, useProductTypes } from '@/features/dictionary';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { attributeValuesPayloadOrder } from '../api/mappers';
import {
  productFormSchema,
  type ProductFormInput,
  type ProductFormOutput,
} from '../schemas/product-form-schema';

export interface ProductSubmitContext {
  /** The options differ from the form's saved ones, so the variants have to follow them. */
  optionsDirty: boolean;
}

/**
 * Receives the validated values, the attribute dictionary the mappers need and what changed.
 * Resolves with what was saved.
 */
export type ProductSubmit = (
  values: ProductFormOutput,
  attributes: readonly Attribute[],
  context: ProductSubmitContext,
) => Promise<unknown>;

export interface UseProductEditorOptions {
  defaultValues: ProductFormInput;
  onSubmit: ProductSubmit;
  isSubmitting: boolean;
  /** The values the form takes after a save, from what `onSubmit` resolved with. */
  reseed?: (saved: unknown) => ProductFormInput | undefined;
  /**
   * The saved product's style number. Regenerating SKUs changes it and retires the old one, so a
   * field nobody typed in follows it. Undefined on the create page.
   */
  savedSkuRoot?: string | null | undefined;
}

export interface ProductEditor {
  form: UseFormReturn<ProductFormInput, unknown, ProductFormOutput>;
  submit: () => Promise<void>;
  attributes: readonly Attribute[];
  attributesById: ReadonlyMap<string, Attribute>;
  productTypes: readonly ProductType[];
  /** The selected product type, once loaded; undefined without one. */
  productType: ProductType | undefined;
  /** True while the selected type or the dictionary is still loading. */
  productTypeLoading: boolean;
  /** The save failure that belongs to no field, for an alert above the cards. */
  rootError: string | undefined;
  /** The person may not change products: the form is disabled and stays off the save bar. */
  readOnly: boolean;
}

/** Whether two form values hold the same data; both come from the same shape, so key order holds. */
const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function readMissing(error: unknown): string[] | null {
  if (!isApiError(error) || !Array.isArray(error.meta.missing)) return null;
  return error.meta.missing.filter((k): k is string => typeof k === 'string');
}

function readVariantLimit(error: unknown): { limit: number; requested: number } | null {
  if (!isApiError(error) || error.code !== 'LIMIT_EXCEEDED') return null;
  const { limit, requested } = error.meta;
  return typeof limit === 'number' && typeof requested === 'number' ? { limit, requested } : null;
}

const ATTRIBUTE_VALUE_FIELD = /^attributeValues\.(\d+)(?:\..*)?$/;

/**
 * Puts the backend's field errors on the form. `body.attributeValues.N` names a position in the
 * payload, not a form field, so it is mapped back to the attribute that sat at N (or to
 * `meta.attributeId` when the backend names it). Returns true when something landed on a field.
 */
function applyProductFieldErrors(
  error: unknown,
  setError: (
    name: FieldPath<ProductFormInput> | `root.${string}`,
    error: { type: string; message: string },
  ) => void,
  sentAttributeIds: readonly string[],
  creating: boolean,
): boolean {
  if (!(error instanceof ApiError) || !error.hasFieldErrors) return false;
  const metaAttributeId =
    typeof error.meta.attributeId === 'string' ? error.meta.attributeId : null;
  let applied = false;
  for (const detail of error.details) {
    const field = formFieldPath(detail.field);
    const match = ATTRIBUTE_VALUE_FIELD.exec(field);
    if (match) {
      const attributeId = metaAttributeId ?? sentAttributeIds[Number(match[1])];
      setError(attributeId ? `attributeValues.${attributeId}` : 'root.server', {
        type: detail.code,
        message: detail.message,
      });
    } else if (field.startsWith('variantSync')) {
      // Only the create page has a field for the price; on a saved product it came from a variant.
      setError(creating ? 'basePrice' : 'root.server', {
        type: detail.code,
        message: detail.message,
      });
    } else {
      setError(field as FieldPath<ProductFormInput>, {
        type: detail.code,
        message: detail.message,
      });
    }
    applied = true;
  }
  return applied;
}

/**
 * Everything the master product form does except where its cards sit: validation, the save
 * through the save bar, backend field errors mapped onto fields (attribute values included),
 * required-attribute errors and the variant limit. The product page lays it out with
 * `ProductForm`; a store's product page places the same cards around its own (ADR 94).
 */
export function useProductEditor({
  defaultValues,
  onSubmit,
  isSubmitting,
  reseed,
  savedSkuRoot,
}: UseProductEditorOptions): ProductEditor {
  const { t } = useTranslation(['products', 'errors']);
  const message = useErrorMessage();
  const { can } = usePermissions();
  const readOnly = !can('products.write');
  const form = useForm<ProductFormInput, unknown, ProductFormOutput>({
    resolver: useI18nResolver(productFormSchema),
    defaultValues,
    disabled: readOnly,
  });

  const attributesQuery = useAttributes();
  const productTypesQuery = useProductTypes();
  const productTypeId = form.watch('productTypeId');
  const productTypeQuery = useProductType(productTypeId || null);

  const attributes = useMemo(() => attributesQuery.data ?? [], [attributesQuery.data]);
  const attributesById = useMemo(() => new Map(attributes.map((a) => [a.id, a])), [attributes]);

  useEffect(() => {
    if (savedSkuRoot === undefined) return;
    const root = savedSkuRoot ?? '';
    const current = form.getValues('skuRoot');
    const typedByHand = current !== form.formState.defaultValues?.skuRoot;
    if (!typedByHand && current !== root) form.resetField('skuRoot', { defaultValue: root });
  }, [form, savedSkuRoot]);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    // A copy: react-hook-form edits nested values in place, and typing goes on during the save.
    const submitted = structuredClone(form.getValues());
    const optionsDirty = !sameValue(submitted.options, form.formState.defaultValues?.options);
    try {
      const result = await onSubmit(values, attributes, { optionsDirty });
      const current = form.getValues();
      form.reset(reseed?.(result) ?? values);
      // Whatever changed while the save ran wasn't in it: it stays on the page, still unsaved.
      for (const name of Object.keys(current) as Array<keyof ProductFormInput>) {
        if (!sameValue(current[name], submitted[name])) {
          form.setValue(name, current[name], { shouldDirty: true });
        }
      }
    } catch (error) {
      const variantLimit = readVariantLimit(error);
      if (variantLimit) {
        form.setError('options', {
          type: 'LIMIT_EXCEEDED',
          message: t('products:optionsCard.limitExceeded', variantLimit),
        });
        return;
      }
      const missing = readMissing(error);
      if (missing) {
        for (const key of missing) {
          const attribute = attributes.find((a) => a.key === key);
          if (attribute) {
            form.setError(`attributeValues.${attribute.id}`, {
              type: 'required',
              message: 'products:validation.requiredForActive',
            });
          }
        }
        form.setError('root.server', {
          type: 'INVALID_INPUT',
          message: t('products:validation.missingRequired', { count: missing.length }),
        });
        return;
      }
      const sent = attributeValuesPayloadOrder(values.attributeValues, attributes);
      if (!applyProductFieldErrors(error, form.setError, sent, values.basePrice !== undefined)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting, disabled: readOnly });

  return {
    form,
    submit,
    attributes,
    attributesById,
    productTypes: productTypesQuery.data ?? [],
    productType: productTypeQuery.data,
    productTypeLoading:
      Boolean(productTypeId) && (productTypeQuery.isPending || attributesQuery.isPending),
    rootError: form.formState.errors.root?.server?.message,
    readOnly,
  };
}
